/**
 * sindhi-cards.js
 * Sindhi Dry Fruits — Product Card Interactions
 * ─────────────────────────────────────────────────────────────────────────────
 * Handles:
 *   1. Weight / variant pill selection  → updates active state, price display,
 *                                         and ATC variant ID
 *   2. Quick-add (hover ATC) + full ATC → AJAX /cart/add with loading state
 *   3. Wishlist toggle                  → localStorage-backed heart icon
 *   4. Live price update                → reads variant data from data-* attrs
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  if (window.__sindhiCardsInit) { return; }
  window.__sindhiCardsInit = true;

  /* ── Config ────────────────────────────────────────────────────────────── */
  var CART_ADD_URL  = '/cart/add.js';
  var CART_EVENT    = 'cart:update';           // dispatched after successful add
  var SUCCESS_MS    = 2200;                    // ms before ATC button resets
  var WISHLIST_KEY  = 'sindhi_wishlist';       // localStorage key

  /* ── Helpers ────────────────────────────────────────────────────────────── */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return (ctx || document).querySelectorAll(sel); }

  function formatMoney(cents) {
    // Shopify formats money in cents; fall back gracefully
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, '₹{{amount}}');
    }
    return '₹' + (cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ── 1. Weight / Variant pill selection ────────────────────────────────── */
  function initWeightPills(card) {
    var pills   = qsa('.sc-w-btn', card);
    var atcBtns = qsa('[data-sc-atc]', card);
    var priceEl = qs('[data-sc-price]', card);
    var oldEl   = qs('[data-sc-price-old]', card);
    var saveEl  = qs('[data-sc-price-save]', card);
    var savePct = qs('[data-sc-save-pct]', card);

    if (!pills.length) return;

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        // Active state
        pills.forEach(function (p) { p.classList.remove('is-active'); p.setAttribute('aria-selected', 'false'); });
        pill.classList.add('is-active');
        pill.setAttribute('aria-selected', 'true');

        // Update variant ID on all ATC buttons in this card
        var vid = pill.getAttribute('data-variant-id');
        if (vid) {
          atcBtns.forEach(function (btn) { btn.setAttribute('data-variant-id', vid); });
        }

        // Update atc-zone variant id so qty counter tracks the right variant
        var zone = card.querySelector('[data-sc-atc-zone]');
        if (zone && vid) {
          zone.setAttribute('data-variant-id', vid);
          updateCardQtyUI(card);
        }

        // Live price update
        var price    = parseInt(pill.getAttribute('data-price'), 10);
        var compare  = parseInt(pill.getAttribute('data-compare-price'), 10);

        if (price && priceEl) {
          priceEl.textContent = formatMoney(price);
        }

        if (oldEl) {
          if (compare && compare > price) {
            oldEl.textContent = formatMoney(compare);
            oldEl.style.display = '';
          } else {
            oldEl.textContent = '';
            oldEl.style.display = 'none';
          }
        }

        if (saveEl && savePct) {
          if (compare && compare > price) {
            var pct = Math.floor((compare - price) * 100 / compare);
            saveEl.textContent = 'Save ' + pct + '%';
            savePct.textContent = '−' + pct + '%';
            saveEl.style.display = '';
            savePct.style.display = '';
          } else {
            saveEl.style.display = 'none';
            savePct.style.display = 'none';
          }
        }

        // Sold-out guard
        var available = pill.getAttribute('data-available') !== 'false';
        atcBtns.forEach(function (btn) {
          btn.disabled = !available;
          btn.textContent = available
            ? (btn.getAttribute('data-label-add') || 'Add to Cart')
            : (btn.getAttribute('data-label-sold') || 'Sold Out');
        });
      });
    });
  }

  /* ── 2. AJAX Add to Cart ────────────────────────────────────────────────── */
  function sindhi_card_atc(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId || btn.disabled) return;

    var origInner   = btn.innerHTML;
    var origBg      = btn.style.background;
    var origColor   = btn.style.color;

    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
      '<path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"/>' +
      '</svg>';

    fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('cart_add_failed');
      return r.json();
    })
    .then(function () {
      btn.classList.remove('is-loading');
      btn.innerHTML        = origInner;
      btn.style.background = origBg;
      btn.style.color      = origColor;
      btn.disabled         = false;
      /* 'product:added' → Sindhi cart drawer refresh + auto-open (if enabled) */
      document.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { id: parseInt(variantId, 10), variantId: variantId }
      }));
      /* Fetch the full cart once, then dispatch Horizon's canonical
         'cart:update' with the { data: {...} } shape its own components
         expect (component-cart-items.js reads event.detail.data.sections,
         cart-icon.js reads event.detail.data.itemCount). A bare
         CustomEvent('cart:update') with no detail throws inside Horizon's
         #handleCartUpdate before it reaches its re-render fallback, which is
         why the cart PAGE silently never reflected a rail add without a
         manual reload — the drawer still worked because it listens for the
         same event but doesn't touch event.detail. */
      fetchCartState(function (cart) {
        syncAllCards();
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { itemCount: cart.item_count, source: 'sindhi-tabbed-rail' } }
        }));
      });
    })
    .catch(function () {
      btn.classList.remove('is-loading');
      btn.innerHTML        = origInner;
      btn.style.background = origBg;
      btn.style.color      = origColor;
      btn.disabled         = false;
    });
  }

  // Expose globally so inline onclick can call it
  window.sindhi_card_atc = sindhi_card_atc;

  /* ── 4. Cart quantity sync ──────────────────────────────────────────────── */
  var CART_QTY_MAP = {};   /* variant_id → quantity */
  var CART_KEY_MAP = {};   /* variant_id → line item key (e.g. "40123:abc") */
  var CART_CHANGE_URL = '/cart/change.js';

  /* Horizon's own header cart bubble is driven by its cart-icon.js, which
     only reacts to a 'cart:update' event carrying detail.data.itemCount.
     The qty-stepper path deliberately does NOT fire that event (it would
     race our own listener — see the comment at the stepper's fetch
     handler), so the bubble kept whatever count the initial ADD set and
     showed line-count-looking values like "1" for a 2-unit cart (real
     bug, reported 2026-07-26). Writing the bubble directly here keeps it
     accurate on every path without reintroducing that race.
     cart.item_count is Shopify's TOTAL UNIT COUNT, not the number of
     line items — which is the number the shopper expects. */
  function _syncNativeCartBubble(cart) {
    var count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
    if (count === null) return;
    var bubble = document.querySelector('.cart-bubble');
    var countEl = document.querySelector('.cart-bubble__text-count');
    if (countEl) {
      countEl.textContent = count < 100 ? String(count) : '';
      countEl.classList.toggle('hidden', count === 0);
    }
    if (bubble) bubble.classList.toggle('visually-hidden', count === 0);
  }

  function _applyCartToMaps(cart) {
    var qmap = {}, kmap = {};
    (cart.items || []).forEach(function(item) {
      var vid = String(item.variant_id);
      qmap[vid] = (qmap[vid] || 0) + item.quantity;
      kmap[vid]  = item.key;   /* always use Shopify line-item key for change.js */
    });
    CART_QTY_MAP = qmap;
    CART_KEY_MAP = kmap;
    _syncNativeCartBubble(cart);
  }

  function fetchCartState(cb) {
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        _applyCartToMaps(cart);
        if (cb) cb(cart);
      })
      .catch(function() {});
  }

  function updateCardQtyUI(card) {
    var zone = card.querySelector('[data-sc-atc-zone]');
    if (!zone) return;
    var vid  = String(zone.getAttribute('data-variant-id') || '');
    var qty  = CART_QTY_MAP[vid] || 0;
    var form = zone.querySelector('form');
    var qtyEl = zone.querySelector('[data-sc-qty]');
    var count = zone.querySelector('[data-sc-qty-count]');
    /* [data-sc-atc-zone] only exists on the floating-ADD branch of
       .sc-qc__add-wrap (qc_atc_mode: 'sheet' cards use a bare trigger
       with no zone, so this function already no-ops for them via the
       early return above) -- so this only ever fires for the "no
       variant sheet, quantity added directly from the card" case.
       The origin badge (.sc-qc__origin-wrap) and the qty stepper are
       both right/left-anchored floats near the same corner; the
       stepper is wider than the ADD button it replaces (88px vs ~56px
       min-width) and started overlapping the badge once an item was
       added. Sanjay: "hide country of origin when no variant sheet is
       used and quantity is added directly from product card." */
    var originWrap = card.querySelector('.sc-qc__origin-wrap');
    if (!qtyEl) return;
    if (qty > 0) {
      if (form) form.hidden = true;
      qtyEl.hidden = false;
      if (count) count.textContent = qty;
      if (originWrap) originWrap.hidden = true;
    } else {
      if (form) form.hidden = false;
      qtyEl.hidden = true;
      if (originWrap) originWrap.hidden = false;
    }
  }

  function syncAllCards() {
    qsa('[class^="sc-v"]').forEach(updateCardQtyUI);
    qsa('[data-sc-qc]').forEach(updateCardQtyUI);
  }

  function initQtyControls(card) {
    var zone = card.querySelector('[data-sc-atc-zone]');
    if (!zone || zone.__qtyInited) return;
    zone.__qtyInited = true;
    zone.addEventListener('click', function(e) {
      var dec = e.target.closest('[data-sc-qty-dec]');
      var inc = e.target.closest('[data-sc-qty-inc]');
      if (!dec && !inc) return;

      var vid    = String(zone.getAttribute('data-variant-id') || '');
      var cur    = CART_QTY_MAP[vid] || 0;
      var newQty = inc ? cur + 1 : Math.max(0, cur - 1);
      /* Use the line-item key — Shopify's change.js uses key, NOT variant int */
      var lineKey = CART_KEY_MAP[vid] || String(parseInt(vid, 10));

      /* Optimistic UI update immediately */
      CART_QTY_MAP[vid] = newQty;
      updateCardQtyUI(card);

      /* Rapid +/- clicks each fired their own independent fetch here, with no
         way to guarantee the LAST click's response was the one that actually
         won: if an earlier click's request resolved after a later one's, its
         (now stale, smaller) quantity overwrote CART_QTY_MAP and visibly
         snapped the counter back down. Real bug, reported by Sanjay: rapid
         quantity increases sometimes decrement instead. window.SindhiCartGuard
         aborts any request still in flight for this variant before starting a
         new one — same guard sindhi-cart-drawer.js and sindhi-variant-sheet.js
         also route through, and every other cart-mutating fetch in this theme
         already used its own copy of (see quick-order-list.js, local-pickup.js). */
      window.SindhiCartGuard.guarded('sindhi-cards', vid, function (signal) {
        return fetch(CART_CHANGE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: lineKey, quantity: newQty }),
          signal: signal
        }).then(function(r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        });
      })
      .then(function(cart) {
        /* Update maps directly from the change.js response — no extra /cart.js
           fetch needed, no race condition */
        _applyCartToMaps(cart);
        syncAllCards();
        /* Notify the cart drawer — 'cart:updated' is what the drawer listens for.
           Do NOT fire 'cart:update' here — that would trigger our own listener
           and start a second fetchCartState, causing a race that reverts the UI. */
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      })
      .catch(function(err) {
        if (err && err.name === 'AbortError') return;
        /* On network error re-fetch to get true state */
        fetchCartState(syncAllCards);
      });
    });
  }

  // Initial cart state load. Also dispatch 'sindhi:cart:updated' (the same
  // event sindhi-offers.js already listens for) once the qty-stepper UI has
  // synced, so a card already in the cart at page load gets its offer
  // price/nudge recomputed for the real quantity instead of showing the
  // SSR qty=1 state until the shopper interacts with it (real bug, found
  // in final-review 2026-07-26 — no other initial-load path fired this
  // event). Note: this also re-triggers the 'sindhi:cart:updated' listener
  // below, causing one harmless extra /cart.js fetch on page load only.
  fetchCartState(function(cart) {
    syncAllCards();
    document.dispatchEvent(new CustomEvent('sindhi:cart:updated', { detail: { cart: cart } }));
  });

  /* Re-sync card UI when the cart changes OUTSIDE this script:
     - 'sindhi:cart:updated' → fired by the cart drawer after its own qty/remove
     - 'cart:update'         → Horizon's canonical event (native PDP add/qty)
     We deliberately do NOT listen for our own 'cart:updated' dispatch (fired by
     the +/- handler) — that handler already re-syncs from the change.js response,
     so listening would cause a redundant /cart.js fetch. */
  document.addEventListener('sindhi:cart:updated', function() { fetchCartState(syncAllCards); });

  /* ── 3. Wishlist toggle ──────────────────────────────────────────────────── */
  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function setWishlist(list) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); }
    catch (e) { /* storage disabled */ }
  }

  function initWishlist(card, wl) {
    // Match any card-variant's wishlist button (.sc-v2__wishlist, .sc-v9__wishlist, etc.)
    var btn = qs('[class*="__wishlist"]', card);
    if (!btn) return;

    var pid = btn.getAttribute('data-product-id');
    if (!pid) return;

    var list = wl || getWishlist();
    if (list.indexOf(pid) > -1) btn.classList.add('is-wishlisted');

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var list = getWishlist();
      var idx  = list.indexOf(pid);

      if (idx > -1) {
        list.splice(idx, 1);
        btn.classList.remove('is-wishlisted');
        btn.setAttribute('aria-label', btn.getAttribute('data-label-add') || 'Add to wishlist');
      } else {
        list.push(pid);
        btn.classList.add('is-wishlisted');
        btn.setAttribute('aria-label', btn.getAttribute('data-label-remove') || 'Remove from wishlist');
      }

      setWishlist(list);
      document.dispatchEvent(new CustomEvent('sindhi:wishlist:change', { detail: { list: list }, bubbles: true }));
    });
  }

  /* ── Init on DOM ready ──────────────────────────────────────────────────── */
  function initCard(card, wl) {
    if (card.__scInit) { return; }
    card.__scInit = true;
    initWeightPills(card);
    initWishlist(card, wl);
    initQtyControls(card);
  }

  function initAll() {
    var wl = getWishlist();
    qsa('[class^="sc-v"]').forEach(function(card) { initCard(card, wl); });
    qsa('[data-sc-qc]').forEach(function(card) { initQtyControls(card); });
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init after AJAX section loads (Shopify Theme Editor / sections API)
  document.addEventListener('shopify:section:load', function (e) {
    var wl = getWishlist();
    qsa('[class^="sc-v"]', e.target).forEach(function(card) { initCard(card, wl); });
    qsa('[data-sc-qc]', e.target).forEach(function(card) { initQtyControls(card); syncAllCards(); });
  });

  // Public API — initialise cards injected after page load (e.g. tabbed-rail recommendations)
  window.SindhiCards = {
    initAll: initAll,
    formatMoney: formatMoney,
    refresh: function (root) {
      var wl = getWishlist();
      qsa('[class^="sc-v"]', root || document).forEach(function (card) { initCard(card, wl); });
      qsa('[data-sc-qc]', root || document).forEach(function (card) { initQtyControls(card); });
      syncAllCards();
    }
  };

})();

/* ── Quick-card variant <select> handler ──────────────────────────────────
   Moved here from snippets/sindhi-card-qc.liquid 2026-08-23. The snippet
   renders once per product card, so this identical function shipped inline
   24-32x per collection page (~37KB of duplicate, uncacheable HTML) even
   though its own guard meant it only ever ran once.

   Kept outside the IIFE above (which early-returns on re-execution) so this
   stays independent of that guard. Invoked from an inline
   onchange="sindhi_card_qc_variant(this)" attribute; sindhi-cards.js is
   loaded with `defer`, which executes before DOMContentLoaded and therefore
   well before a user can interact with the select.
   ───────────────────────────────────────────────────────────────────────── */
if (!window.sindhi_card_qc_variant) {
  window.sindhi_card_qc_variant = function (sel) {
    var card = sel.closest('[data-sc-qc]');
    if (!card) return;
    var opt = sel.options[sel.selectedIndex];
    var vid = opt.value;
    var price = parseInt(opt.getAttribute('data-price') || '0', 10);
    var comp  = parseInt(opt.getAttribute('data-compare-price') || '0', 10);
    var avail = opt.getAttribute('data-available') === 'true';
    var priceEl = card.querySelector('[data-sc-price]');
    var oldEl   = card.querySelector('[data-sc-price-old]');
    var atc     = card.querySelector('[data-sc-atc]');
    var fmt = window.SindhiCards && window.SindhiCards.formatMoney;
    if (priceEl && fmt) {
      priceEl.textContent = fmt(price);
    }
    if (oldEl) {
      if (comp > price && fmt) {
        oldEl.textContent = fmt(comp);
        oldEl.style.display = '';
      } else {
        oldEl.style.display = 'none';
      }
    }
    card.querySelectorAll('[data-variant-id]').forEach(function (el) { el.setAttribute('data-variant-id', vid); });
    var zone = card.querySelector('[data-sc-atc-zone]');
    if (zone) zone.setAttribute('data-variant-id', vid);
    var hidden = card.querySelector('input[name="id"]');
    if (hidden) hidden.value = vid;
    if (atc) {
      if (!avail) {
        atc.setAttribute('disabled', 'disabled');
        atc.textContent = atc.getAttribute('data-label-sold') || 'Sold';
      } else {
        atc.removeAttribute('disabled');
        atc.textContent = atc.getAttribute('data-label-add') || 'ADD';
      }
    }
  };
}
